import { Location, SetLoading, SystemAccess, UserType } from "@arema/components/Classes"
import { IconName } from "react-frontier2"

export enum ConciliaStatus{
	AVAILABLE = 1,
	ALREADY = 2, // Already added previously
	INCOMPLETE = 3,
	TOO_MUCH = 4,
	MISSING = 99,
}

export enum DepositType{
	DEPOSIT = 1,
	REFUND = 10,
	CHARGEBACK = 20,
}

export enum CancelReason{
	GENERIC = 1,
	EXPIRED = 2,
	REFUNDED = 3,
	REPLACED = 4,
	MOVE_REPLACED = 5,
}

export interface ConciliaDeposit{
	order_id: number,
	payment_id: number,
	status: ConciliaStatus,
	external_id: string,
	total: number,
	amount: number
	payment_method: number,
	deposit_type: DepositType,
	deposit_total: number,
	deposit_refund_total: number,
	current_deposit_total: number,
	current_deposit_refund_total: number,
	date_deposit: number,
	card_type?: CardType,
	completed?: boolean,
}

export interface InvalidParsedOrder{
	deposit_type: DepositType,
	payment_method: number,
	external_id: string,
	amount: number,
	date_deposit: number,
	card_type?: CardType
}

export enum CardType{
	CREDIT = 1,
	DEBIT = 2,
	UNKNOWN = 99,
}

export interface ParsedOrder extends InvalidParsedOrder{
	order_id: number,
	payment_id: number,
}

export interface FoundExternalDeposit{
	deposit_id: number,
	payment_id: number,
	external_id: string,
}

export interface BankAccount{
	account_id: number,
	name_account: string,
	rfc: string,
	curp?: string,
	bank?: string,
	bank_participant?: number,
	clabe: string,
	card_number: string,
	postal_code?: string,
	email_account?: string,
	date_created?: number,
}

export enum PayoutType{
	NORMAL = 1,
	ADELANTO = 10,
	RETORNO = 20,
	OTHER = 255,
}

export interface ListPayout{
	payout_id: number,
	event_id: number,
	payout_type: PayoutType,
	payout_type_name: number,
	event_name: string,
	amount: number,
	dates: number,
	approved: boolean,
	completed: boolean,
	rejected: boolean,
	cancelled: boolean,
	bank_account: number,
	bank: string,
	clabe: string,
	card_number: string,
	user_creator: number,
	user_creator_name: string,
	date_scheduled: number,
	date_created: number,
	date_completed: number,
	date_cancelled: number,
	date_approved: number,
}

export interface ListPayoutGroup{
	group_id: number,
	bank_id: number,
	bank_name: string,
	approved: boolean,
	payouts: number,
	date_created: number,
	date_scheduled: number,
	date_approved: number,
	amount: number,
	date_completed: number,
}

export enum AccountOwnerType{
	FISICA = 1,
	MORAL = 2,
}

export interface ListRefund {
	refund_id: number,
	order_id: number,
	refund_type: number,
	direct_refund?: boolean,
	total: number,
	cancelled: boolean
	total_tickets: number,
	completed: boolean,
	date_created: number,
	date_completed?: number,
	date_cancelled?: number,
}

export interface TicketTemplate{
	template_id: number,
	template_name: string,
	description: string,
	ticket_width: number,
	ticket_height: number,
	zpl: string,
	user_creator: number,
	deleted: boolean,
	date_created: number,
	date_deleted: number,
}

export interface PrintTicket{
	ticket_id: number,
	event_name: string,
	venue_name: string,
	promoter_name: string,
	order_id: number,
	ticket_date: number,
	date_id: number,
	date_paid: number,
	ticket_cost: number,
	price_id: number,
	price_name: string,
	section_id: number,
	section_name: string,
	seat_id: number,
	seat_row: string,
	seat_number: string,
	seat_section: string,
	ticket_hash: string,
	order_hash: string,
	seat_format: string,
}

export interface PapeletaCurrency{
	deposit: number,
	refund: number,
	chargeback: number,
}
interface PapeletaDataEvent{
	event_id: number, 
	event_name: string,
	deposit: number,
	refund: number,
	chargeback: number,
}

export interface PapeletaPdvData{
	pdv_id: number,
	pdv_name: string,
	pdv_type: PDVType,
	events: {
		event_id: number,
		event_name: string,
		cortes: {
			corte_id: number,
			amount: number,
		}[],
	}[]
	deposits: {
		deposit_id: number,
		date_deposit: number,
		deposit_type: PDVDepositTypes,
		source_id: number,
		source_name: string,
		cortes: {
			corte_id: number,
			amount: number,
		}[]
	}[],
	cortes: {
		corte_id: number,
		amount_sales: number,
		amount_deposit: number,
	}[],
}

export interface ReportDownloadLink{
	text: string,
	icon?: IconName,
	url: string,
}

export interface ReportResponse<T>{
	downloads: ReportDownloadLink[],
	data: T[]
}

export interface OrderInvoice{
	invoice_id: number,
	amount: number,
	rfc: string,
	legal_name: string,
	date_created: number,
	date_cancelled: number,
	cancelled: boolean,
	download_pdf?: string,
	download_zip?: string,
}

export interface OrderEmails{
	email_id: number,
	type_name: string,
	email: string,
	date_created: number,
	delivered: boolean,
}

export interface InvoiceList extends OrderInvoice {
	order_hash: string,
}

export interface UserReportsEvent{
	event_id: number,
	event_name: string,
	date_added: number,
	access: number[],
}

export interface UserReportsPromoter{
	promoter_id: number,
	promoter_name: string,
	date_added: number,
	access: number[],
}

export interface BankBin{
	bank_id?: number,
	bank_name: string,
	description: string,
	deleted?: boolean,
	user_creator?: string,
	user_deleted?: number,
	user_deleted_name?: string,
	date_created?: number,
	date_deleted?: number,
	bins?: {
		number_id: number,
		bin: string
	}[]
}

export interface ExternalSearchOrder{
	order_id: number,
	payment_id: number,
	total: number,
	current_deposit_total: number,
	current_deposit_refund_total: number,
}

export interface ReportsAccessType{
	access_id: number,
	access_name: string,
}

export interface BusinessBank{
	bank_id: number
	bank_name: string,
	deleted: boolean,
	user_creator: number,
	user_deleted: number,
	date_created: number,
	date_deleted: number,
}

export enum BusinessBankType{
	NORMAL = 1,
	STP = 2,
}

export interface PDVZone{
	zone_id?: number,
	zone_name: string,
	description?: string,
	date_added: number,
	user_creator: number,
	user_creator_name: string,
}

export interface CorteOrders{
	order_id: number,
	user_creator: number,
	date_created: number,
	event_name: string,
	payment_method_name: string,
	total: number,
	user_creator_name: string,
	date_id:  number,
}

export interface CorteDeposits{
	deposit_id: number,
	date_deposit: number,
	description: string,
	amount: number,
}

export interface PdvCorte{
	pdv_id: number,
	corte_id: number,
	date_start: number,
	order_count: number,
	order_cash_count: number,
	total_cash: number,
	total: number,
	concilia_cash: number,
	deposit: number,
	concilia_percent: number,
	concilia_percent_cash: number,
}

export interface PdvDepositAmount {
	amount_id?: number,
	corte_id: number,
	date_start: number,
	pdv_id: number,
	order_count: number,
	cash: number,
	total: number,
	deposit: number,
}

export enum DepositSourceType{
	AREMA = 1,
	TICKETLOCK = 2,
}

export interface DepositSource {
	source_id: DepositSourceType,
	source_name: string,
}

export enum SourceBusiness{
	AREMA = 1,
	TICKETLOCK = 2,
}

export interface PdvDepositSource {
	source_id?: number,
	source_name: string,
	deleted?: number,
}

export enum PDVType{
	TAQUILLA = 1,
	INTERNAL = 150,
	TESTING = 151,
}

export interface PdvStats{
	pdv_id: number,
	pdv_name: string,
	pdv_type: PDVType,
	total: number,
	total_cash: number,
	deposit: number,
}

export interface CorteOrdersConcilia {
	payment_id: number,
	payment_method: string,
	order_id: number,
	event_name: string,
	total: number,
	total_deposit: number,
	date_paid: number,
}

export interface PaymentTerminalType {
	type_id: number,
	type_name: string
}

export enum PaymentTerminalTypes{
	AREMA_PAY_APP = 1,
	GETNET_API = 2,
}

export interface PaymentTerminal {
	terminal_id?: number,
	terminal_name: string,
	terminal_type: PaymentTerminalTypes,
	terminal_serial: string,
	type_name?: string,
	terminal_token?: string,
	terminal_secret?: boolean,
	deleted?: boolean,
	user_creator?: number,
	user_deleted?: number,
	user_deleted_name?: string,
	date_created?: number,
	date_deleted?: number,
	license_id?: number,
	license_name?: string,
}

export interface PaymentTerminalLicense {
	license_id: number,
	license_name: string,
}

export interface Zone {
	zone_id?: number,
	zone_name: string,
	description?: string,
	date_created?: number,
	date_deleted?: number,
	deleted?: boolean,
}

export interface SectionHold{
	hold_id?: number,
	date_id: number,
	section_id: number,
	amount: string,
	comments: string,
}

export interface Scanner {
	scanner_id?: number,
	scanner_name: string,
	date_start?: number,
	date_end?: number,
	token?: string,
	admin_creator?: number,
	all_events: boolean,
	all_sections: boolean,
	deleted?: boolean,
	date_deleted?: number,
	events?: {
		event_id: number,
		event_name: string,
	}[],
	sections?: {
		section_id: number,
		section_name: string,
		venue_id: number,
		venue_name: string,
		city: string,
	}[]
}

export interface PDV {
	pdv_id?: number,
	pdv_name: string,
	location?: Location,
	token: string,
	deleted?: boolean,
	date_deleted?: number,
	all_events?: boolean,
	venue_name?: string,
	venue_id?: number,
	external_terminal: boolean,
	exclude_commission: boolean,
	report_access: boolean,
	ignore_limits: boolean,
	money_supervised: boolean,
	terminal_name?: string,
	terminal_id?: number,
	pdv_type: number,
}

export interface PromoterContact {
	contact_id?: number,
	promoter_id: number,
	contact_name: string,
	department: string,
	email: string,
	phone: string,
}

export interface TicketUse{
	use_id: number,
	ticket_id: number,
	used: boolean,
	message: string,
	scanner_id: number,
	scanner_name?: string,
	deleted: boolean,
	user_creator: number,
	user_creator_name?: string,
	user_deleted?: number,
	date_created: number,
	date_deleted?: number,
}

export interface OrderConcilia{
	deposit_id: number,
	payment_id: number,
	deposit_type: DepositType,
	payment_method: number,
	amount: number,
	external_id?: string,
	cancelled: boolean,
	user_creator: number,
	date_deposit: number,
	date_created: number,
	date_cancelled: number,
	payment_method_name: string,
	deposit_type_name: string,
}

export interface OrderRefund{
	refund_id: number,
	order_id: number,
	order_hash: string,
	refund_type: number,
	delivery_cost: number,
	payment_cost: number,
	executed: boolean,
	completed: boolean,
	cancelled: boolean,
	date_created: number,
	date_cancelled: number,
	date_completed: number,
	total: number,
	total_tickets: number,
}

export interface OrderPayment{
	payment_id: number,
	order_id: number,
	payment_hash?: string,
	pdv_id: number,
	corte_id?: number,
	payment_method: number,
	method_name?: string,
	method_name_internal?: string,
	total: number,
	total_refunded: number,
	total_available: number,
	payment_cost: number,
	delivery_cost: number,
	executed: boolean,
	paid: boolean,
	cancelled?: boolean,
	external_id: string,
	reference?: string,
	user_creator: number,
	date_executed: number,
	date_paid: number,
	date_expiration?: number,
	date_created: number,
	date_cancelled?: number,
}

export interface OrderEmail{
	email_id: number,
	order_id: number,
	email_type: number,
	service_id: number,
	email: string,
	external_id: string,
	delivered: boolean,
	deleted: boolean,
	date_created: number,
	date_delivered: number,
	date_deleted: number,
	service_name?: string,
	type_name?: string,
}

export interface Ticket{
	ticket_id: number,
	order_id: number,
	pdv_id: number,
	price_id: number,
	seat_id: number,
	ticket_cost: number,
	commission: number,
	ticket_date: number,
	paid: boolean,
	cancelled: boolean,
	cancel_reason: CancelReason,
	hidden: boolean,
	printed: boolean,
	uses: number,
	code: string,
	date_created: number,
	date_paid: number,
	date_expiration: number,
	date_cancelled: number,
	card_commission?: number,
	payment_id: number,
	ticket_hash?: string,
	event_id?: number,
	event_name?: string,
	seat_number: string,
	seat_row: string,
	seat_section: string,
	section_name: string,
	price_name: string,
	refunded?: number,
	amount_refunded?: number,
	commission_refunded?: number,
	date_id?: number,
	user_creator?: number,
	user_creator_name?: string,
	user_cancelled?: number,
	user_cancelled_name?: string,
	gift_id?: number,
	[x: string]: any,
}

export interface Order{
	order_id: number,
	pdv_id: number,
	date_id: number,
	first_name: string,
	last_name: string,
	email: string,
	phone: string,
	commission: number,
	delivery_cost: number,
	total_tickets: number,
	payment_method: number,
	payment_method_name: string,
	payment_method_name_internal: string,
	cancelled: boolean
	hidden: boolean,
	delivery_id: number,
	delivered: boolean
	delivery_location: number,
	delivery_name: string,
	delivery_needs_location: boolean,
	delivery_type: number,
	legacy_id: number,
	code: string,
	form_answered: boolean,
	reference: string,
	creator_ip: string,
	date_created: number,
	date_cancelled: number,
	order_hash?: string,
	pdv_name?: string,
	user_creator: number,
	user_creator_name?: string,
	payments?: OrderPayment[],
	tickets?: Ticket[],
	emails?: OrderEmail[],
	concilia?: OrderConcilia[],
	refunds?: OrderRefund[],
	// [x: string]: any,
}

export interface EventData{
	event_id: number,
	promoter_id: number,
	admin_id: number,
	event_type: number,
	category_id: number,
	event_name: string,
	subdomain: string,
	access_code: string,
	published: boolean,
	secret: boolean,
	sinopsis: string,
	deleted: boolean,
	date_created: number,
	date_publish: number,
	date_deleted: number,
	dynamic_code: boolean,
	promoter_name?: string,
	producer_name?: string,
	category_name?: string,
	type_name?: string,
	google_tag?: string,
	meta_pixel?: string,
	tiktok_pixel?: string,
	user_assigned?: number,
	user_assigned_name?: string,
	// secret: boolean,
	commission_return: number,
	date_start: number,
	zone_id: number,
	zone_name: string,
	bank_id: number,
	bank_name: string,
	has_map?: boolean,
}

export interface EventSectionProps<T=any>{
	event: EventData,
	onEditEvent: (event: EventData)=>void,
	params?: T
}
export interface UserClass{
	user_id: number,
	password: string,
	first_name: string,
	last_name: string,
	email: string,
	user_type: UserType,
	user_secret: number,
	superadmin: boolean,
	active: boolean,
	change_password: boolean,
	email_confirmed: boolean,
	user_creator: number,
	date_created: number,
	date_ban: number,
	access?: number[],
	systems?: {
		[x in SystemAccess]: boolean
	}
}

export interface PDVDepositType{
	type_id: number,
	description: string,
}

export enum PDVDepositTypes{
	DEPOSIT = 1,
	PROMOTER_CASH = 20,
	REFUNDS = 21,
	TICKET_PRINTS = 22,
}

export interface SearchOrder{
	order_hash: string,
	date: string,
	pdv_id: number,
	event_name: string,
	first_name: string,
	last_name: string,
	email: string,
	cancelled: boolean,
	paid: boolean,
	expired: boolean,
	total: number,
}

export interface AccountingAmexPayment{
	card_bin: number,
	card_digits: number,
	total: number,
	reference: string
}

export interface PayfrontAccountingPayment{
	payment_id: number,
	external_id: string,
	paid: boolean,
	total: number,
	date_paid: number,
	card_bin: number,
	card_last4: number,
	reference: string
}

export enum AssetType{
	IMAGE = 1,
	PDF = 2,
}

export interface AssetFile{
	asset_id: number,
	asset_name: string,
	type_id: AssetType,
	extension: string,
	date_created: number,
	download_url?: string,
}

export interface AssetRelations{
	payouts?: number[],
	refunds?: number[],
	deposits?: number[],
}

export interface AcquirerService{
	service_id: number,
	service_name: string,
}

export interface Promoter {
	promoter_id: number,
	promoter_name: string,
	user_assigned: number,
	user_assigned_name: string,
}

export enum RefundType{
	MANUAL = 1,
	REQUEST = 10,
	MARK_COMPLETE = 20,
	CANCELLED = 250,
}

export interface ReportItem<T>{
	title: string,
	description: string,
	icon: IconName,
	ask_date_range?: boolean,
	shown?: boolean,
	ask_event?: boolean,
	ask_date?: boolean,
	single_date?: boolean,
	button_text?: string,
	button_href?: string,
	onClick?: (setLoading: SetLoading)=>void,
	onDownload?: (p: T)=>Promise<string>,
}

export interface IncomeReportData{
	event_id: number,
	event_name: string,
	promoter_id: number,
	promoter_name: string,
	user_assigned: number,
	user_assigned_name: string,
	ticket_income: number,
	commission: number,
	total_return: number,
	delivery_cost: number,
	payment_cost: number,
}